import { Component, DestroyRef, inject, input, OnDestroy, OnInit, signal, ChangeDetectionStrategy, viewChild } from '@angular/core';
import {
	CostGroup,
	Exemption,
	ExemptionService,
	Flatrate,
	FlatrateService,
	TenantUnitReference,
	TenantUnitReferenceService,
} from '@eadsysteme/billingclient-v1';
import { FlatrateExemptionService } from './flatrates-exemption.service';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import {
	MatCell,
	MatCellDef,
	MatColumnDef,
	MatHeaderCell,
	MatHeaderCellDef,
	MatHeaderRow,
	MatHeaderRowDef,
	MatNoDataRow,
	MatRow,
	MatRowDef,
	MatTable,
	MatTableDataSource,
} from '@angular/material/table';
import { MatIcon } from '@angular/material/icon';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButton, MatFabButton, MatIconButton } from '@angular/material/button';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { MatSort, MatSortHeader } from '@angular/material/sort';
import { DateUtil } from '../../../../../util/DateUtil';
import { MatDialog, MatDialogActions } from '@angular/material/dialog';
import { LayoutService } from '../../../../../../@vex/services/layout.service';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { fadeInUp400ms } from '../../../../../../@vex/animations/fade-in-up.animation';
import { stagger40ms } from '../../../../../../@vex/animations/stagger.animation';
import { SessionService } from '../../../../../services/session.service';
import { SnackbarService } from '../../../../../services/snackbar.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { forkJoin } from 'rxjs';
import { FlatrateExemptionDialogComponent } from './flatrate-exemption-dialog/flatrate-exemption-dialog.component';
import { FlatrateExemptionDetail } from './FlatrateExemptionDetail';
import { ConfirmationDialogComponent } from '../../../../confirmation-dialog/confirmation-dialog.component';

@Component({
	selector: 'vex-flatrates-exemptions',
	imports: [
		TranslocoDirective,
		MatCell,
		MatCellDef,
		MatColumnDef,
		MatHeaderCell,
		MatHeaderRow,
		MatHeaderRowDef,
		MatRow,
		MatRowDef,
		MatTable,
		MatHeaderCellDef,
		MatNoDataRow,
		TranslocoDirective,
		MatIcon,
		ReactiveFormsModule,
		MatIconButton,
		MatMenu,
		MatMenuItem,
		MatMenuTrigger,
		MatSort,
		MatSortHeader,
		MatButton,
		MatDialogActions,
		MatFormField,
		MatLabel,
		FormsModule,
		MatInput,
		MatFabButton,
	],
	templateUrl: './flatrates-exemptions.component.html',
	styleUrl: './flatrates-exemptions.component.scss',
	changeDetection: ChangeDetectionStrategy.Eager,
	animations: [fadeInUp400ms, stagger40ms],
})
export class FlatratesExemptionsComponent implements OnInit, OnDestroy {
	private flatrateService = inject(FlatrateService);
	private exemptionService = inject(ExemptionService);
	private destroyRef = inject(DestroyRef);
	private snackBarService = inject(SnackbarService);
	private dialog = inject(MatDialog);
	private translocoService = inject(TranslocoService);
	protected sessionService = inject(SessionService);
	protected tenantUnitReferenceService = inject(TenantUnitReferenceService);
	protected dateUtil = inject(DateUtil);
	protected flatratesExemptionsService = inject(FlatrateExemptionService);
	protected layoutService = inject(LayoutService);

	// Not .required: the MatSort may be absent (the code guards with `if (this.sort())`), so a
	// required query would throw NG0951 when it is read while unresolved/absent.
	readonly sort = viewChild(MatSort);
	protected hideAll = signal(false);

	costGroup = input<CostGroup>(null);
	billingId = input('');
	periodId = input('');
	protected dataSource: MatTableDataSource<FlatrateExemptionDetail>;
	protected columns: string[] = ['tenantUnit', 'validFrom', 'validTo'];
	private tenantUnits = signal<TenantUnitReference[]>(null);
	showFlatratesExemptionsOfCostGroup = input<any>(undefined);

	ngOnInit(): void {
		if (!this.showFlatratesExemptionsOfCostGroup()) {
			// if no costGroup is given here, it means the component of a not clicked costgroup is loaded, so we prevent the rendering of anything
			this.hideAll.set(true);
			return;
		}
		if (this.isFlatrate) {
			this.columns.push('value');
		}
		this.columns.push('actions');

		this.loadDatasource();
		this.closeOnEscapeKey();
	}

	ngOnDestroy() {
		document.removeEventListener('keydown', this.handleEscKey);
	}

	private handleEscKey = (event: KeyboardEvent): void => {
		if (event.key === 'Escape') {
			this.back();
		}
	};

	private closeOnEscapeKey() {
		document.addEventListener('keydown', this.handleEscKey);
	}

	private loadDatasource() {
		const loadData$ = this.isFlatrate
			? this.flatrateService.readAllFlatrate(this.sessionService.effectiveArea, this.billingId(), this.costGroup().exportId)
			: this.exemptionService.readAllExemption(this.sessionService.effectiveArea, this.billingId(), this.costGroup().exportId);

		const loadTenantUnits$ = this.tenantUnitReferenceService.readAllTenantUnitReference(this.sessionService.effectiveArea, this.billingId());

		forkJoin({ exemptionsFlatrates: loadData$, tenantUnits: loadTenantUnits$ })
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe({
				next: (data) => this.updateDataSource(data),
				error: () => this.snackBarService.error(this.translocoService.translate('billing.flatrates.loadDataFailed')),
			});
	}

	private updateDataSource(data: { exemptionsFlatrates: (Exemption | Flatrate)[]; tenantUnits: TenantUnitReference[] }) {
		this.tenantUnits.set(data.tenantUnits);
		const tenantunitsById = new Map(data.tenantUnits.map((tu) => [tu.exportId, tu]));

		const mappedData = data.exemptionsFlatrates.map((item) =>
			this.mapToFlatrateExemptionDetail(item, tenantunitsById.get(item.tenantUnitReferenceExportId))
		);

		this.dataSource = new MatTableDataSource(mappedData);
		if (this.sort()) {
			this.sortDatasource();
		}
	}

	private sortDatasource() {
		this.dataSource.sort = this.sort();
		this.dataSource.sortingDataAccessor = (item: FlatrateExemptionDetail, property: string) => {
			switch (property) {
				case 'tenantUnit': {
					const num = item.tenantUnit?.number ?? 0;
					const subNum = item.tenantUnit?.subNumber ?? 0;
					return num * 1000 + subNum;
				} // z.B. 1/0 = 1000, 1/2 = 1002, 3/0 = 3000

				case 'validFrom':
					return item.validFrom ? new Date(item.validFrom).getTime() : 0;

				case 'validTo':
					return item.validTo ? new Date(item.validTo).getTime() : 0;

				case 'value':
					return item.value ?? 0;

				default:
					return (item as any)[property];
			}
		};
	}

	back() {
		this.flatratesExemptionsService.deactivateView();
	}

	protected new() {
		this.openDialog(true, null);
	}

	protected edit(row: FlatrateExemptionDetail) {
		this.openDialog(false, row);
	}

	private openDialog(isNew: boolean, objectToEdit: FlatrateExemptionDetail | null) {
		const dialogData = {
			billingId: this.billingId(),
			costGroupId: this.costGroup().exportId,
			tenantUnits: this.tenantUnits(),
			objectToEdit,
			isNew,
		};

		const matDialogRef = this.dialog.open(FlatrateExemptionDialogComponent, {
			disableClose: true,
			width: '600px',
			data: dialogData,
		});

		matDialogRef.afterClosed().subscribe((saved) => {
			if (!saved) return;

			const action = isNew ? 'added' : 'updated';
			const type = this.isFlatrate ? 'Flatrate' : 'Exemption';
			this.snackBarService.success(this.translocoService.translate(`billing.${action}${type}`));
			this.loadDatasource();
		});
	}

	protected delete(row: FlatrateExemptionDetail) {
		const type = this.isFlatrate ? 'Flatrate' : 'Exemption';
		const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
			disableClose: true,
			autoFocus: false,
			restoreFocus: false,
		});
		dialogRef.componentInstance.titleMessage = this.translocoService.translate('common.attention');
		dialogRef.componentInstance.confirmMessage = this.translocoService.translate(`billing.delete${type}Confirmation`);

		dialogRef.afterClosed().subscribe(async (result) => {
			if (result) {
				const delete$ = this.isFlatrate
					? this.flatrateService.deleteFlatrate(this.sessionService.effectiveArea, this.billingId(), row.id)
					: this.exemptionService.deleteExemption(this.sessionService.effectiveArea, this.billingId(), row.id);

				delete$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
					next: () => {
						this.snackBarService.success(this.translocoService.translate(`billing.delete${type}Success`));
						this.loadDatasource();
					},
					error: () => {
						this.snackBarService.error(this.translocoService.translate(`billing.delete${type}Failed`));
					},
				});
			}
		});
	}

	mapToFlatrateExemptionDetail(source: Flatrate | Exemption, tenantUnit?: TenantUnitReference): FlatrateExemptionDetail {
		return {
			id: source.exportId,
			validFrom: source.validFrom,
			validTo: source.validTo,
			value: 'value' in source ? source.value : undefined,
			tenantUnitReferenceId: source.tenantUnitReferenceExportId,
			tenantUnit: tenantUnit && {
				id: tenantUnit.exportId,
				number: tenantUnit.number,
				subNumber: tenantUnit.subNumber,
				name: tenantUnit.name,
			},
		};
	}

	private get isFlatrate(): boolean {
		return this.flatratesExemptionsService.selectedType() === 'FLATRATE';
	}
}
